import { CalculationType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  ConditionValidation,
  CustomColumnField,
  ReportField,
} from "../../../../../../api/types/svTypes";
import { ItemDataType, ReportType } from "../../../../../../api/types/svTypes";
import { isAllowedInConditions } from "../../utilities/conditionDataTypes.ts";
import {
  canBeReferencedInCustomFormula,
  hasValidConditionReference,
  isAggregation,
  isCustomColumn,
  isDateAggregationField,
  isEligibleDateScopeField,
  isMeasure,
  isMetric,
} from "../../utilities/fieldsHelper";

export const validateCondition = (
  field: ConditionField,
  conditions: ConditionField[],
  reportType?: ReportType
): ConditionValidation => {
  if (!isAllowedInConditions(field.meta, reportType)) {
    const allowedTypes = field.meta.options?.conditions?.allowedReportTypes;
    const supportsConditions = field.meta.type === ItemDataType.General || field.meta.type === ItemDataType.Date;
    return {
      invalid: true,
      message:
        supportsConditions && allowedTypes?.length
          ? `This condition is only available in ${allowedTypes.join(", ")} reports and is not applied.`
          : "This dimension cannot be used in conditions and is not applied.",
    };
  }
  if (reportType === ReportType.List || field.hasLinks) {
    return { invalid: false };
  }
  const linkedConditions = conditions.filter(
    (v) => v.meta.name === field.meta.name && v.config.guid !== field.config.guid && v.hasLinks
  );
  if (linkedConditions.length > 0) {
    return {
      invalid: true,
      message: "The condition is not active. To use it, link it as a custom condition to a value field.",
    };
  }

  const notLinkedConditions = conditions.filter((v) => v.meta.name === field.meta.name && !v.hasLinks);
  if (notLinkedConditions.length === 1 || notLinkedConditions.indexOf(field) === 0) {
    return { invalid: false };
  }
  return {
    invalid: true,
    message: "Another condition for this dimension is already active. This duplicate condition is not applied.",
  };
};

export const updateLinkAndValidate = (
  conditions: ConditionField[],
  measures: ReportField[],
  reportType?: ReportType
): ConditionField[] => {
  conditions.forEach((condition) => {
    const index = measures.findIndex(
      (v) =>
        ((isMeasure(v) && v.meta.capabilities.customConditionsAllowed) || isAggregation(v) || isMetric(v)) &&
        v.config.useCustomConditions &&
        v.config.linkedConditions &&
        v.config.linkedConditions.indexOf(condition.config.guid) > -1
    );
    condition.hasLinks = index > -1;
  });
  conditions.forEach((condition) => {
    condition.validation = validateCondition(condition, conditions, reportType);
  });

  return conditions;
};

export const validateCustomFields = (fields: ReportField[], conditions: ConditionField[] = []): ReportField[] => {
  return fields.map((field) => {
    if (isCustomColumn(field)) {
      field.invalid = !isCustomFieldValid(fields, conditions, field);
    } else if (isDateAggregationField(field)) {
      field.invalid = !isDateAggregationFieldValid(fields, field);
    }
    return field;
  });
};

const isDateAggregationFieldValid = (fields: ReportField[], field: ReportField): boolean => {
  const referencedField = fields.find((f) => f.config.guid === field.config.dependsFromFieldId);
  return isEligibleDateScopeField(referencedField);
};

const isCustomFieldValid = (fields: ReportField[], conditions: ConditionField[], field: CustomColumnField): boolean => {
  if (!field.config.formulaParameters || field.config.formulaParameters.length === 0) {
    return false;
  }
  if (field.config.calculationType === CalculationType.ConditionValue) {
    return hasValidConditionReference(field, conditions);
  }
  return field.config.formulaParameters.every((parameter) => {
    const referencedField = fields.find((f) => f.config.guid === parameter);
    if (!referencedField) {
      return false;
    }
    return canBeReferencedInCustomFormula(referencedField);
  });
};

export const updateTableField = (item: ReportField, changedValues: Partial<ReportField>, fields: ReportField[]) => {
  const field = fields.find((field) => field.config.guid === item.config.guid);
  if (field !== undefined) {
    Object.assign(field, changedValues);
  }
  return fields;
};

export const updateTableFieldsOrder = (fields: ReportField[], guids: string[]) => {
  return guids
    .map((guid) => {
      return fields.find((field) => field.config.guid === guid);
    })
    .filter((field): field is ReportField => !!field);
};
